<template>
  <list-page-shell>
    <template #actions>
      <el-button size="small" @click="setPreset('month')">{{ $t('common.thisMonth') }}</el-button>
      <el-button size="small" @click="setPreset('year')">{{ $t('common.thisYear') }}</el-button>
      <el-button size="small" @click="load">{{ $t('common.refresh') }}</el-button>
      <el-button size="small" type="primary" @click="exportXlsx">{{ $t('salesPersonReportPage.export') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small">
        <el-form-item v-if="isOfficeLike" :label="$t('salesPersonReportPage.salesperson')">
          <el-select
            v-model="filters.salesUserId"
            clearable
            filterable
            :placeholder="$t('salesPersonReportPage.allSales')"
            style="width: 220px;"
            @change="onSalesChange"
          >
            <el-option
              v-for="s in salesOptions"
              :key="s.value"
              :label="s.label"
              :value="s.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isOfficeLike">
          <el-checkbox v-model="filters.onlyUnassigned" @change="onUnassignedChange">
            {{ $t('salesPersonReportPage.onlyUnassigned') }}
          </el-checkbox>
        </el-form-item>
        <el-form-item :label="$t('common.date')">
          <el-date-picker
            v-model="range"
            type="daterange"
            value-format="yyyy-MM-dd"
            :start-placeholder="$t('common.dateRangeStart')"
            :end-placeholder="$t('common.dateRangeEnd')"
            style="width: 320px;"
          />
        </el-form-item>
      </el-form>
    </template>

    <el-alert v-if="detail.salespersonLabel" type="info" :closable="false" show-icon style="margin-bottom: 14px;">
      <template slot="title">
        {{ $t('salesPersonReportPage.scopeLabel') }}：
        <strong>{{ detail.salespersonLabel }}</strong>
        · {{ detail.rangeStart }} — {{ detail.rangeEnd }}
      </template>
    </el-alert>

    <el-card shadow="never" style="margin-bottom: 14px;">
      <div slot="header">{{ $t('salesPersonReportPage.sectionLeads') }}</div>
      <el-table :data="detail.leads || []" border size="small" max-height="320">
        <el-table-column v-if="showSalesCol" prop="salesperson" :label="$t('salesPersonReportPage.colSales')" min-width="110" />
        <el-table-column prop="leadNo" :label="$t('salesPersonReportPage.colLeadNo')" width="120" />
        <el-table-column prop="firstName" :label="$t('salesPersonReportPage.colFirstName')" width="100" />
        <el-table-column prop="lastName" :label="$t('salesPersonReportPage.colLastName')" width="100" />
        <el-table-column prop="email" :label="$t('salesPersonReportPage.colEmail')" min-width="160" />
        <el-table-column prop="statusCode" :label="$t('salesPersonReportPage.colStatus')" width="90" />
        <el-table-column prop="createdAt" :label="$t('salesPersonReportPage.colCreatedAt')" min-width="160" />
      </el-table>
    </el-card>

    <el-card shadow="never" style="margin-bottom: 14px;">
      <div slot="header">{{ $t('salesPersonReportPage.sectionPendingQuotes') }}</div>
      <el-table :data="detail.pendingQuotes || []" border size="small" max-height="280">
        <el-table-column v-if="showSalesCol" prop="salesperson" :label="$t('salesPersonReportPage.colSales')" min-width="110" />
        <el-table-column prop="quoteNo" :label="$t('salesPersonReportPage.colQuoteNo')" width="120" />
        <el-table-column prop="leadNo" :label="$t('salesPersonReportPage.colLeadNo')" width="120" />
        <el-table-column prop="quoteStatusCode" :label="$t('salesPersonReportPage.colQuoteStatus')" width="110" />
        <el-table-column prop="updatedAt" :label="$t('salesPersonReportPage.colUpdatedAt')" min-width="160" />
      </el-table>
    </el-card>

    <el-card shadow="never" style="margin-bottom: 14px;">
      <div slot="header">{{ $t('salesPersonReportPage.sectionPendingContracts') }}</div>
      <el-table :data="detail.pendingContracts || []" border size="small" max-height="280">
        <el-table-column v-if="showSalesCol" prop="salesperson" :label="$t('salesPersonReportPage.colSales')" min-width="110" />
        <el-table-column prop="contractNo" :label="$t('salesPersonReportPage.colContractNo')" width="120" />
        <el-table-column prop="quoteNo" :label="$t('salesPersonReportPage.colQuoteNo')" width="120" />
        <el-table-column prop="leadNo" :label="$t('salesPersonReportPage.colLeadNo')" width="120" />
        <el-table-column prop="contractStatusCode" :label="$t('salesPersonReportPage.colContractStatus')" width="110" />
        <el-table-column prop="updatedAt" :label="$t('salesPersonReportPage.colUpdatedAt')" min-width="160" />
      </el-table>
    </el-card>

    <el-card shadow="never">
      <div slot="header">{{ $t('salesPersonReportPage.sectionSignedContracts') }}</div>
      <el-table :data="detail.signedContracts || []" border size="small" max-height="320">
        <el-table-column v-if="showSalesCol" prop="salesperson" :label="$t('salesPersonReportPage.colSales')" min-width="110" />
        <el-table-column prop="contractNo" :label="$t('salesPersonReportPage.colContractNo')" width="120" />
        <el-table-column prop="quoteNo" :label="$t('salesPersonReportPage.colQuoteNo')" width="120" />
        <el-table-column prop="leadNo" :label="$t('salesPersonReportPage.colLeadNo')" width="120" />
        <el-table-column prop="contractStatusCode" :label="$t('salesPersonReportPage.colContractStatus')" width="110" />
        <el-table-column prop="signedAt" :label="$t('salesPersonReportPage.colSignedAt')" min-width="160" />
        <el-table-column prop="signedValueUsd" :label="$t('salesPersonReportPage.colSignedValueUsd')" width="130" align="right" />
      </el-table>
    </el-card>
  </list-page-shell>
</template>

<script>
import axios from 'axios'
import ListPageShell from '@/components/ListPageShell.vue'
import { getSalesPersonDetail } from '@/api/dashboard'
import { getSalesUserList } from '@/api/user'
import { resolveApiBase } from '@/utils/apiBase'

const baseURL = resolveApiBase()

export default {
  name: 'SalesPersonReport',
  components: { ListPageShell },
  data() {
    return {
      isOfficeLike: false,
      salesOptions: [],
      filters: {
        salesUserId: '',
        onlyUnassigned: false
      },
      range: [],
      detail: {
        salespersonLabel: '',
        rangeStart: '',
        rangeEnd: '',
        leads: [],
        pendingQuotes: [],
        pendingContracts: [],
        signedContracts: [],
        includeSalesColumn: false
      }
    }
  },
  computed: {
    showSalesCol() {
      return !!this.detail.includeSalesColumn
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteQuery()
      },
      deep: true
    }
  },
  async created() {
    const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
    this.isOfficeLike = roles.includes('OFFICE') || roles.includes('ADMIN')
    if (this.isOfficeLike) {
      await this.loadSalesOptions()
    }
    this.initRangeDefault()
    this.applyRouteQuery()
    await this.load()
  },
  methods: {
    initRangeDefault() {
      const z = new Date()
      const y = z.getFullYear()
      const m = String(z.getMonth() + 1).padStart(2, '0')
      const d = String(z.getDate()).padStart(2, '0')
      const end = `${y}-${m}-${d}`
      const start = `${y}-${m}-01`
      this.range = [start, end]
    },
    applyRouteQuery() {
      const q = this.$route.query || {}
      if (q.onlyUnassigned === '1' || q.onlyUnassigned === 'true') {
        this.filters.onlyUnassigned = true
        this.filters.salesUserId = ''
      } else if (q.salesUserId != null && q.salesUserId !== '') {
        this.filters.onlyUnassigned = false
        this.filters.salesUserId = Number(q.salesUserId)
      }
    },
    onSalesChange() {
      if (this.filters.salesUserId !== '' && this.filters.salesUserId != null) {
        this.filters.onlyUnassigned = false
      }
    },
    onUnassignedChange(val) {
      if (val) {
        this.filters.salesUserId = ''
      }
    },
    async loadSalesOptions() {
      try {
        const resp = await getSalesUserList()
        const rows = (resp && resp.list) || []
        this.salesOptions = rows.map(r => ({
          value: r.id,
          label: (r.realName || r.username || '').trim() || this.$t('common.unnamed')
        }))
      } catch (e) {
        this.salesOptions = []
      }
    },
    params() {
      const [startDate, endDate] = this.range || []
      const p = {}
      if (startDate) p.startDate = startDate
      if (endDate) p.endDate = endDate
      if (this.isOfficeLike && this.filters.onlyUnassigned) {
        p.onlyUnassigned = true
      } else if (this.isOfficeLike && this.filters.salesUserId !== '' && this.filters.salesUserId != null) {
        p.salesUserId = this.filters.salesUserId
      }
      return p
    },
    setPreset(which) {
      const z = new Date()
      const y = z.getFullYear()
      const m = z.getMonth()
      const d = z.getDate()
      const pad = n => String(n).padStart(2, '0')
      if (which === 'month') {
        const start = `${y}-${pad(m + 1)}-01`
        const end = `${y}-${pad(m + 1)}-${pad(d)}`
        this.range = [start, end]
      } else if (which === 'year') {
        const start = `${y}-01-01`
        const end = `${y}-${pad(m + 1)}-${pad(d)}`
        this.range = [start, end]
      }
      this.load()
    },
    async load() {
      try {
        const raw = await getSalesPersonDetail(this.params())
        const d = raw.data !== undefined ? raw.data : raw
        if (raw.code && raw.code !== 200) {
          this.$message.error(raw.msg || this.$t('common.failed'))
          return
        }
        this.detail = {
          salespersonLabel: d.salespersonLabel || '',
          rangeStart: d.rangeStart || '',
          rangeEnd: d.rangeEnd || '',
          leads: d.leads || [],
          pendingQuotes: d.pendingQuotes || [],
          pendingContracts: d.pendingContracts || [],
          signedContracts: d.signedContracts || [],
          includeSalesColumn: !!d.includeSalesColumn
        }
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      }
    },
    async exportXlsx() {
      const token = localStorage.getItem('token')
      try {
        const res = await axios.get(`${baseURL}/api/dashboard/sales-person-detail-export`, {
          params: this.params(),
          responseType: 'blob',
          headers: { Authorization: 'Bearer ' + token }
        })
        const blob = res.data
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'sales-person-report.xlsx'
        a.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      }
    }
  }
}
</script>
